import { useMemo } from "react";
import {
  MRT_GlobalFilterTextField,
  MRT_TableBodyCellValue,
  MRT_TablePagination,
  MRT_ToolbarAlertBanner,
  flexRender,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Grid,
} from "@mui/material";
import TableSkeletonLoader from "./tableSkeletionLoader";

const DataTableWithSearch = ({ columns, data, renderRowActions, renderFilters, searchFilter, status }) => {

  // Add serial number  
  const dataWithSno = useMemo(
    () => data.map((item, index) => ({ ...item, sno: index + 1 })),
    [data]
  );

  // Inject actions column
  const columnsWithActions = useMemo(() => {
    if (!renderRowActions) return columns;

    return [
      ...columns,
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => renderRowActions({ row }),
      },
    ];
  }, [columns, renderRowActions]);

  const table = useMaterialReactTable({
    columns: columnsWithActions,
    data: dataWithSno,
    enableRowSelection: false,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      showGlobalFilter: true,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [
        5,
        10,
        15,
        { label: "All", value: data.length },
      ],
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
  });

  if (["loading", "pending", "fetching"].includes(status)) {
    return (
      <Box sx={{ p: 2 }}>
        {renderFilters && <Box mb={2}>{renderFilters}</Box>}
        <TableSkeletonLoader columnsCount={columns.length || 6} rowsCount={10}  table={table}/>
      </Box>
    );
  }


  return (
    <Stack spacing={2}>
      {/* Filters and Global Search */}
      <Grid container spacing={1} mb={2}>
        <Grid item size={{ xs: 12, md: 8 }}>{renderFilters}</Grid>
        <Grid item size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {searchFilter !== false && <MRT_GlobalFilterTextField table={table} />}
        </Grid>
      </Grid>

      {/* Scrollable Table */}
      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'auto',
          overflowY: 'auto',
          "&::-webkit-scrollbar": { height: "6px", width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
          scrollbarWidth: "thin",
          scrollbarColor: "#888 #f1f1f1",
        }}
      >
        
        <Table
          stickyHeader
          sx={{
            width: '100%',
            minWidth: '1200px',
            tableLayout: 'auto', // header defines width
          }}
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "capitalize",
                      backgroundColor: header.id === "actions" ? "#ffe0b2" : "var(--primary)",
                      color: header.id === "actions" ? "#ff9800" : "#fff",
                      position: header.id === "actions" ? "sticky" : "static",
                      right: header.id === "actions" ? 0 : undefined,
                      zIndex: header.id === "actions" ? 100 : 1,
                      whiteSpace: "nowrap",
                      maxWidth: 'clamp(180px, 30vw, 600px)',
                      padding: "6px",
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      borderRight: "2px solid rgba(226, 224, 224, 0.8)",
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  align="center"
                  sx={{ padding: "20px" }}
                >
                  No Data Found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <TableRow key={row.id} selected={row.getIsSelected()}>
                    {row.getVisibleCells().map((cell) => {
                      const cellValue = cell.getValue();
                      const isDateColumn = cell.column.id !== "date"; // wrap only for date column
                      return (
                        <TableCell
                          key={cell.id}
                          align="center"
                          sx={{
                            border: "1px solid rgba(226, 224, 224, 0.5)",
                            position: cell.column.id === "actions" ? "sticky" : "static",
                            right: cell.column.id === "actions" ? 0 : undefined,
                            backgroundColor: cell.column.id === "actions" ? "#fff" : "inherit",
                            zIndex: cell.column.id === "actions" ? 100 : 0,
                            whiteSpace: isDateColumn ? "normal" : "nowrap",
                            wordBreak: isDateColumn ? "break-word" : "ellipsis",
                            maxWidth: 'clamp(180px, 30vw, 600px)',
                            overflow: 'hidden',
                            padding: "5px",
                          }}
                        >
                          {cell.column.id === "actions"
                            ? flexRender(
                              cell.column.columnDef.Cell,
                              cell.getContext()
                            )
                            : cellValue === null ||
                              cellValue === undefined ||
                              String(cellValue).trim() === ""
                              ? "-"
                              : (
                                <MRT_TableBodyCellValue
                                  cell={cell}
                                  table={table}
                                  staticRowIndex={rowIndex}
                                />
                              )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <MRT_TablePagination table={table} />
      </Box>

      <MRT_ToolbarAlertBanner stackAlertBanner table={table} />
    </Stack>
  );
};

export default DataTableWithSearch;
